import { useState, useEffect, useCallback, useMemo } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { useTranslation } from '@/i18n'
import AppSidebar from '@/components/AppSidebar'
import { apiService } from '@/service/api'
import type { DocumentRecord } from '@/types/catalog'
import type { CardPositions, Connection, PendingConnection } from './types'
import PidSetNavbar from './PidSetNavbar'
import PidSetSubNavbar from './PidSetSubNavbar'
import PageMappingCanvas from './PageMappingCanvas'
import SetPreviewOverlay from './SetPreviewOverlay'

/** Compute staggered 2-column layout from ordered document list */
function computeAutoLayout(docs: DocumentRecord[]): CardPositions {
  const positions: CardPositions = {}
  docs.forEach((doc, i) => {
    const col = i % 2
    const row = Math.floor(i / 2)
    positions[doc.id] = {
      x: col === 0 ? 260 : 700,
      y: col === 0 ? 100 + row * 180 : 180 + row * 180,
    }
  })
  return positions
}

export default function PidSetViewer() {
  const { t } = useTranslation()
  const { set_id } = useParams<{ set_id: string }>()
  const [searchParams] = useSearchParams()

  const [documents, setDocuments] = useState<DocumentRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [setName, setSetName] = useState('')
  const [activeTab, setActiveTab] = useState('page-mapping')
  const [cardPositions, setCardPositions] = useState<CardPositions>({})
  const [connections, setConnections] = useState<Connection[]>([])
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null)
  const [previewDoc, setPreviewDoc] = useState<DocumentRecord | null>(null)
  const [pendingConnection, setPendingConnection] = useState<PendingConnection | null>(null)

  // Fetch documents for this set
  useEffect(() => {
    if (!set_id) return
    let cancelled = false

    async function fetchSetDocuments() {
      setLoading(true)
      try {
        // Primary: use docIds from URL params (passed from data catalog)
        const docIdsParam = searchParams.get('docIds')
        let docs: DocumentRecord[]

        if (docIdsParam) {
          const ids = docIdsParam.split(',').filter(Boolean)
          docs = ids.length > 0
            ? await apiService.queryDocs({ type: undefined, doc_ids: ids })
            : []
        } else {
          // Fallback: fetch all pnid sets and filter by set_id
          const response = await apiService.listDocsPaginated({
            type: 'pnid',
            is_set: true,
            page_size: 200,
          })
          docs = response.documents.filter(
            (d) => d.properties?.multi_page_set_id === set_id
          )
        }

        if (cancelled) return

        // Sort by sheet_index
        docs.sort(
          (a, b) => (Number(a.properties?.sheet_index) || 0) - (Number(b.properties?.sheet_index) || 0)
        )

        setDocuments(docs)
        setSetName(docs[0]?.properties?.multi_page_set_name || t('graph_editor:pidSet.unnamedSet', { defaultValue: 'Unnamed Set' }))
        setCardPositions(computeAutoLayout(docs))
      } catch {
        // Error handled silently — documents will be empty
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    fetchSetDocuments()
    return () => { cancelled = true }
  }, [set_id, searchParams])

  const handleCardMove = useCallback((docId: string, pos: { x: number; y: number }) => {
    setCardPositions((prev) => ({ ...prev, [docId]: pos }))
  }, [])

  const handleConnectionAdd = useCallback((sourceId: string, targetId: string) => {
    setConnections((prev) => [
      ...prev,
      { id: `${sourceId}-${targetId}`, sourceDocId: sourceId, targetDocId: targetId },
    ])
  }, [])

  const handleCardPreview = useCallback((doc: DocumentRecord) => {
    setPreviewDoc(doc)
  }, [])

  const closePreview = useCallback(() => {
    setPreviewDoc(null)
  }, [])

  const tabContent = useMemo(() => {
    if (activeTab !== 'page-mapping') {
      return (
        <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
          {t('graph_editor:pidSet.viewNotAvailable', { defaultValue: 'This view is not yet available.' })}
        </div>
      )
    }
    return (
      <PageMappingCanvas
        documents={documents}
        cardPositions={cardPositions}
        onCardMove={handleCardMove}
        connections={connections}
        onConnectionAdd={handleConnectionAdd}
        selectedCardId={selectedCardId}
        onCardSelect={setSelectedCardId}
        onCardPreview={handleCardPreview}
        pendingConnection={pendingConnection}
        onPendingConnectionChange={setPendingConnection}
      />
    )
  }, [activeTab, documents, cardPositions, handleCardMove, connections, handleConnectionAdd, selectedCardId, handleCardPreview, pendingConnection, t])

  if (loading) {
    return (
      <div className="flex h-screen">
        <AppSidebar />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="size-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-screen">
      <AppSidebar />
      <div className="flex flex-1 flex-col min-w-0">
        <PidSetNavbar setName={setName} />
        <PidSetSubNavbar activeTab={activeTab} onTabChange={setActiveTab} />
        {tabContent}
      </div>

      {/* Preview overlay */}
      {previewDoc && (
        <SetPreviewOverlay
          docId={previewDoc.id}
          docName={previewDoc.name}
          onClose={closePreview}
        />
      )}
    </div>
  )
}
